import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DataTable, Notice } from './patterns';
import type { DataTableColumn } from './patterns';

interface Orchard {
  _id: string;
  orchardName: string;
  farmName: string;
}

const LegacyMigrationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [unlinkedBlocks, setUnlinkedBlocks] = useState<string[]>([]);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});

  const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InVzZXIxIiwidGVuYW50SWQiOiJ0ZW5hbnQxMjMiLCJpYXQiOjE3NzA0NTIyMTF9.p0QS4h5rJY4PWFboy8vBzSCjo3dW-awd8c4aPwKKq_Q';
  const apiConfig = {
    headers: { 'Authorization': `Bearer ${mockToken}` }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocksRes, orchardsRes] = await Promise.all([
        axios.get('/api/v1/migration/unlinked-blocks', apiConfig),
        axios.get('/api/v1/orchards', apiConfig)
      ]);
      setUnlinkedBlocks(blocksRes.data);
      setOrchards(orchardsRes.data);
    } catch (err: any) {
      setError('Failed to fetch migration data');
    } finally {
      setLoading(false);
    }
  // apiConfig references mockToken which is a module-level constant; no deps needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectChange = (blockId: string, orchardId: string) => {
    setMappings(prev => ({ ...prev, [blockId]: orchardId }));
  };

  const handleAlign = async (blockId: string) => {
    const orchardId = mappings[blockId];
    if (!orchardId) return;

    setLoading(true);
    try {
      const response = await axios.post('/api/v1/migration/align', {
        oldBlockId: blockId,
        newOrchardId: orchardId
      }, apiConfig);

      setSuccess(`Successfully linked ${blockId}. Updated ${response.data.updatedCount} records.`);
      setUnlinkedBlocks(prev => prev.filter(b => b !== blockId));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(`Failed to link ${blockId}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && unlinkedBlocks.length === 0) {
    return (
      <div className="legacy-migration__loading">
        {t('legacyMigration.loading')}
      </div>
    );
  }

  const columns: DataTableColumn<string>[] = [
    {
      key: 'block',
      label: t('legacyMigration.legacyBlock'),
      render: (blockId) => <code>{blockId}</code>,
    },
    {
      key: 'target',
      label: t('legacyMigration.targetOrchard'),
      render: (blockId) => (
        <select
          className="legacy-migration__select"
          value={mappings[blockId] || ''}
          onChange={(e) => handleSelectChange(blockId, e.target.value)}
        >
          <option value="">{t('legacyMigration.selectOrchard')}</option>
          {orchards.map(o => (
            <option key={o._id} value={o._id}>
              {o.orchardName} ({o.farmName})
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'action',
      label: t('legacyMigration.action'),
      render: (blockId) => (
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          disabled={!mappings[blockId] || loading}
          onClick={() => handleAlign(blockId)}
        >
          {t('legacyMigration.linkRecords')}
        </button>
      ),
    },
  ];

  return (
    <div className="legacy-migration">
      <div className="legacy-migration__intro">
        <h3 className="legacy-migration__heading">{t('legacyMigration.heading')}</h3>
        <p className="legacy-migration__help">{t('legacyMigration.help')}</p>
      </div>

      {error && (
        <Notice tone="danger" action={<button type="button" className="legacy-migration__alert-close" aria-label="Close" onClick={() => setError(null)}>×</button>}>
          <span>{error}</span>
        </Notice>
      )}
      {success && (
        <Notice tone="success" role="alert" action={<button type="button" className="legacy-migration__alert-close" aria-label="Close" onClick={() => setSuccess(null)}>×</button>}>
          <span>{success}</span>
        </Notice>
      )}

      <DataTable<string>
        columns={columns}
        rows={unlinkedBlocks}
        rowKey={(blockId) => blockId}
        emptyState={
          <div className="legacy-migration__empty">{t('legacyMigration.noData')}</div>
        }
      />
    </div>
  );
};

export default LegacyMigrationDashboard;
